import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { ignoreParkedDeposit } from '@/services/api/parked-deposits';

interface IgnoreDialogProps {
  transactionId: string;
  /** Ignore succeeded — the page closes this dialog and refetches the list */
  onCompleted: () => void;
  onClose: () => void;
}

export const IgnoreDialog = ({ transactionId, onCompleted, onClose }: IgnoreDialogProps) => {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const handleSave = () => {
    // Deliberate fix: legacy silently no-oped on a blank reason after
    // the justification dialog closed — validate BEFORE opening it instead.
    if (!reason.trim()) {
      setReasonError('Reason for Ignoring is required');
      return;
    }
    setReasonError(null);
    void confirmAction({
      title: 'Confirm Ignore Parked Deposit',
      description: 'Proceeding with this action will automatically ignore the selected parked deposit. Your action here will be logged and will be auditable.',
      actionLabel: 'Ignore Parked Deposit',
      destructive: true,
      // A throw keeps the justification dialog open showing the API error
      // (legacy only console.logged ignore failures)
      onConfirm: async (justification) => {
        await ignoreParkedDeposit({ transactionId, ignoringReason: reason, comment: justification });
        toast.success(`Parked deposit ${transactionId} ignored`);
        onCompleted();
      },
    });
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Ignore Transaction</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="ignoring-reason">Reason for Ignoring</Label>
          <Input
            aria-invalid={reasonError ? true : undefined}
            id="ignoring-reason"
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(null);
            }}
            value={reason}
          />
          {reasonError ? <p className="text-sm text-red-500">{reasonError}</p> : null}
        </div>
        <DialogFooter>
          <Button onClick={onClose} type="button" variant="destructive">
            Cancel
          </Button>
          <Button onClick={handleSave} type="button" variant="primary">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
IgnoreDialog.displayName = 'IgnoreDialog';
